import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { checkAuthStatus } from "./store/slices/affiliatesReducer";
import AppHeader from "./components/AppHeader";
import DashboardPage from "./pages/DashboardPage";
import HomePage from "./pages/HomePage";

import CategoryPage from "./pages/CategoryPage";
import ItemPage from "./pages/ItemPage";
import MenyerPage from "./pages/MenyerPage";
import ReportsCenterPage from "./pages/ReportsCenterPage";
import LandingPage from "./pages/LandingPage";
import SettingsPage from "./pages/SettingsPage";
import PublicDishInfoPage from "./pages/PublicDishInfoPage";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import CreateAffiliates from "./pages/Admin/CreateAffiliates";
import UserManagement from "./pages/Admin/UserManagement";
import Analytics from "./pages/Admin/Analytics";
import PatientListPage from "./pages/KostData/PatientListPage";
import PatientProfilePage from "./pages/KostData/PatientProfilePage";
import MealCreationPage from "./pages/KostData/MealCreationPage";
import MealsListPage from "./pages/KostData/MealsListPage";
import DietitianDashboardPage from "./pages/KostData/DietitianDashboardPage";

const AppRoutes = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Get authentication state from Redux
  const { auth, loading, authChecked } = useSelector((state) => state.affiliate);
  const isAuthenticated = !!auth;
  const isAdmin = auth?.affiliate_role === "admin";

  // Menyer uses its own full-page navbar (MenyerPage); elsewhere use AppHeader
  const shouldShowAppHeader = !location.pathname.includes("/menyer");
  const isPublicDishRoute = location.pathname.startsWith('/public/items/');

  // Check auth status only once when the app loads
  useEffect(() => {
    dispatch(checkAuthStatus());
  }, [dispatch]);

  // Handle route changes with smooth transitions
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Truly public route: bypass auth-role routing branches (no opacity transition — avoids flicker with stable layouts)
  if (isPublicDishRoute) {
    return (
      <div>
        <Routes>
          <Route path="/public/items/:shareToken" element={<PublicDishInfoPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }

  // Show loading until we've checked auth at least once
  if (loading || !authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-gray-200 rounded-full animate-pulse"></div>
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-secondary-green border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-6 text-gray-600 font-medium animate-pulse-slow">Checking authentication...</p>
          <div className="mt-4 flex justify-center space-x-2">
            <div className="w-2 h-2 bg-secondary-green rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-secondary-green rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-secondary-green rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Public routes (no authentication required)
  if (!isAuthenticated) {
    return (
      <div className={`transition-all duration-300 ease-out ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    );
  }

  // Admin routes
  if (isAdmin) {
    return (
      <div className={`transition-all duration-300 ease-out ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
        <Routes>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<UserManagement />} />
          <Route path="/admin/users/create" element={<CreateAffiliates />} />
          <Route path="/admin/analytics" element={<Analytics />} />
          <Route path="/admin/settings" element={<SettingsPage />} />
          <Route path="/login" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    );
  }

  // Check if user is a dietitian
  const isDietitian = auth?.affiliate_type === 'dietitian';

  // Dietitian routes (kostData + regular dish creation)
  if (isDietitian) {
    return (
      <div className={`transition-all duration-300 ease-out ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
        {shouldShowAppHeader && <AppHeader />}
        <div className={shouldShowAppHeader ? "pt-20 tablet:pt-24 laptop:pt-24 desktop:pt-24" : ""}>
          <Routes>
            {/* Dietitian Dashboard */}
            <Route path="/" element={<DietitianDashboardPage />} />
            
            {/* kostData routes */}
            <Route path="/kostdata/patients" element={<PatientListPage />} />
            <Route path="/kostdata/patients/:patientId" element={<PatientProfilePage />} />
            <Route path="/kostdata/meals" element={<MealsListPage />} />
            <Route path="/kostdata/meals/new" element={<MealCreationPage />} />
            <Route path="/kostdata/meals/:mealId/edit" element={<MealCreationPage />} />
            
            {/* Regular affiliate routes (Nivå 1: Categories -> Recipes -> Dishes) */}
            <Route path="/categories" element={<DashboardPage />} />
            <Route path="/menyer" element={<Navigate to="/kostdata/meals" replace />} />
            <Route path="/categories/:id" element={<CategoryPage />} />
            <Route path="/items/:id" element={<ItemPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/reports" element={<ReportsCenterPage />} />
            
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    );
  }

  // Regular affiliate routes
  return (
    <div className={`transition-all duration-300 ease-out ${isTransitioning ? 'opacity-50' : 'opacity-100'}`}>
      {shouldShowAppHeader && <AppHeader />}
      <div className={shouldShowAppHeader ? "pt-20 tablet:pt-24 laptop:pt-24 desktop:pt-24" : ""}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/menyer" element={<MenyerPage />} />

          <Route path="/categories/:id" element={<CategoryPage />} />
          <Route path="/items/:id" element={<ItemPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/reports" element={<ReportsCenterPage />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
};

export default AppRoutes;
